import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useMutation } from '@tanstack/react-query';
import { CoaImportOutcomeSchema, type CoaImportOutcome } from '@taqon/contracts';
import { useState, type ChangeEvent } from 'react';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { payloadFromWorkbook } from './coa-workbook';
import { readWorkbook } from './xlsx';
import { ApiError, postJson } from '../../shared/api/client';
import { PageHeader } from '../../shared/ui/page';
import { useSession } from '../../shared/session/session';

/**
 * COA import (FRD-FIN-015, FIN-016). The approved COA workbook (`.xlsx`) is uploaded as it is:
 * its `COA Master` and `Validation Lists` sheets become the import payload in the browser, shown
 * below for review. The backend is authoritative: **no validation rule is reproduced here**. The
 * page posts the payload, and renders whatever the API's outcome report says — including every
 * rejected row, with the workbook row it came from.
 *
 * A dry run persists nothing; Apply writes the accepted rows. Both use the same endpoint and the
 * same server-side validation.
 */
export function CoaImportPage() {
  const session = useSession();
  const [payload, setPayload] = useState('');
  const [outcome, setOutcome] = useState<{ outcome: CoaImportOutcome; dryRun: boolean } | null>(
    null,
  );
  const [parseError, setParseError] = useState<string | null>(null);
  const [workbook, setWorkbook] = useState<{ name: string; sheetRows: readonly number[] } | null>(
    null,
  );

  /** Reads the chosen workbook into the payload; nothing is sent until Dry run or Apply. */
  const upload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file === undefined) return;
    setParseError(null);
    setOutcome(null);
    try {
      const read = payloadFromWorkbook(await readWorkbook(await file.arrayBuffer()));
      setPayload(JSON.stringify(read.payload, null, 2));
      setWorkbook({ name: file.name, sheetRows: read.sheetRows });
    } catch (e) {
      setWorkbook(null);
      setParseError(e instanceof Error ? e.message : 'The workbook could not be read.');
    }
  };

  const csrfToken = session.data?.csrfToken ?? '';

  const submit = useMutation({
    mutationFn: async (dryRun: boolean) => {
      const body: unknown = JSON.parse(payload);
      const request = typeof body === 'object' && body !== null ? { ...body, dryRun } : { dryRun };
      const result = await postJson(
        '/api/v1/ledger/coa-import',
        request,
        CoaImportOutcomeSchema,
        csrfToken,
      );
      return { outcome: result, dryRun };
    },
    onSuccess: (result) => {
      setOutcome(result);
    },
  });

  const run = (dryRun: boolean) => {
    setParseError(null);
    setOutcome(null);
    try {
      JSON.parse(payload);
    } catch {
      setParseError('That is not valid JSON. Paste the import payload and try again.');
      return;
    }
    submit.mutate(dryRun);
  };

  const error = submit.error;

  return (
    <Stack spacing={3}>
      <PageHeader
        title="COA import"
        description="Upload the Finance-approved COA workbook (.xlsx). A dry run validates every row and saves nothing; Apply writes the accepted rows. Rejected rows are never partially applied."
      />

      <Paper sx={{ p: 2 }}>
        <Stack direction="row" spacing={2} sx={{ mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button component="label" variant="outlined" startIcon={<UploadFileIcon />}>
            Upload COA workbook
            <input
              hidden
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              aria-label="COA workbook"
              onChange={(event) => {
                void upload(event);
              }}
            />
          </Button>
          {workbook !== null && (
            <Typography variant="body2" color="text.secondary">
              {workbook.name}: {workbook.sheetRows.length} account rows read. Review, then Dry run.
            </Typography>
          )}
        </Stack>
        <TextField
          label="Import payload (JSON)"
          multiline
          minRows={10}
          fullWidth
          value={payload}
          onChange={(e) => {
            setPayload(e.target.value);
          }}
          slotProps={{ htmlInput: { 'aria-label': 'Import payload JSON', spellCheck: false } }}
        />
        <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            disabled={submit.isPending || payload.trim() === ''}
            onClick={() => {
              run(true);
            }}
          >
            Dry run
          </Button>
          <Button
            variant="contained"
            disabled={submit.isPending || payload.trim() === ''}
            onClick={() => {
              run(false);
            }}
          >
            Apply
          </Button>
        </Stack>
      </Paper>

      {parseError !== null && <Alert severity="warning">{parseError}</Alert>}

      {error && (
        <Alert severity="error">
          <AlertTitle>The import was refused</AlertTitle>
          {error.message}
          {error instanceof ApiError && error.fieldErrors.length > 0 && (
            <ul>
              {error.fieldErrors.map((message) => (
                <li key={message}>{message}</li>
              ))}
            </ul>
          )}
        </Alert>
      )}

      {outcome && <Outcome outcome={outcome.outcome} sheetRows={workbook?.sheetRows ?? null} />}
    </Stack>
  );
}

function Outcome({
  outcome,
  sheetRows,
}: {
  outcome: CoaImportOutcome;
  /** For an uploaded workbook, the sheet row of each payload row. */
  sheetRows: readonly number[] | null;
}) {
  const counts: [string, number][] = [
    ['Accepted', outcome.accepted],
    ['Rejected', outcome.rejected],
    ['Groups created', outcome.groupsCreated],
    ['Groups reused', outcome.groupsReused],
    ['Accounts created', outcome.accountsCreated],
    ['Accounts revised', outcome.accountsRevised],
    ['Accounts unchanged', outcome.accountsUnchanged],
  ];

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" component="h2" gutterBottom>
        {outcome.dryRun ? 'Dry run result — nothing was saved' : 'Import applied'}
      </Typography>
      <Alert severity={outcome.rejected > 0 ? 'warning' : 'success'} sx={{ mb: 2 }}>
        {outcome.rejected > 0
          ? `${String(outcome.rejected)} row(s) rejected, ${String(outcome.accepted)} accepted.`
          : `All ${String(outcome.accepted)} row(s) accepted.`}
      </Alert>

      <Table size="small" aria-label="Import counts">
        <TableBody>
          {counts.map(([label, value]) => (
            <TableRow key={label}>
              <TableCell component="th" scope="row" sx={{ width: 220 }}>
                {label}
              </TableCell>
              <TableCell>{value}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {outcome.rejections.length > 0 && (
        <>
          <Typography variant="h6" component="h3" sx={{ mt: 3 }} gutterBottom>
            Rejected rows
          </Typography>
          <Table size="small" aria-label="Rejected rows">
            <TableHead>
              <TableRow>
                <TableCell>Row</TableCell>
                {sheetRows !== null && <TableCell>Workbook row</TableCell>}
                <TableCell>Account code</TableCell>
                <TableCell>Reasons</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {outcome.rejections.map((rejection) => (
                <TableRow key={rejection.rowNumber}>
                  <TableCell>{rejection.rowNumber}</TableCell>
                  {sheetRows !== null && (
                    <TableCell>{sheetRows[rejection.rowNumber - 1] ?? '—'}</TableCell>
                  )}
                  <TableCell>{rejection.accountCode ?? '—'}</TableCell>
                  <TableCell>
                    <ul style={{ margin: 0, paddingInlineStart: '1.2em' }}>
                      {rejection.reasons.map((reason) => (
                        <li key={`${reason.rule}:${reason.field ?? ''}`}>
                          <strong>{reason.rule}</strong>
                          {reason.field !== null && ` (${reason.field})`} — {reason.message}
                        </li>
                      ))}
                    </ul>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}
    </Paper>
  );
}
